import getRouting from './routing';
import strings from "assets/strings";
import Text from "components/text/Text";
import Popup from 'components/popup/Popup';
import { OuterServiceResultType } from 'types';
import { localAttributes } from 'assets/constants';
import type { BaseHit, Hit } from 'instantsearch.js';
import React, { useState, useEffect, useRef } from 'react';
import { OuterServicesResult } from './OuterServicesResult';
import styles from './OuterServicesSearchResults.module.scss';
import SvgGenerator from 'components/svgGenerator/SvgGenerator';
import { VirtualQuery } from 'components/virtualQuery/VirtualQuery';
import { CustomSortBy } from 'components/customSortBy/CustomSortBy';
import { VirtualSortBy } from 'components/virtualSortBy/virtualSortBy';
import { FilterButton } from 'components/buttons/filterButton/FilterButton';
import { CustomSortDropdown } from 'components/customSortDropdown/CustomSortDropdown';
import { VirtualRefinementList } from 'components/virtualRefinement/VirtualRefinmentList';
import { MEDICALCENTERS, MEDICALCENTERS_SORT, leumitSearchClient } from 'assets/constants';
import { OuterServicesRefinements } from './OuterServicesRefinements/OuterServicesRefinements';
import { checkIfParamExist, getAllClassNames, getParams, isHebrewText, sanitizeString } from 'helpers';
import {Configure,Index,InstantSearch,UseInfiniteHitsProps,useInfiniteHits,useInstantSearch,useStats,} from 'react-instantsearch';

//import { Loader } from "assets/images";

const routing = getRouting(MEDICALCENTERS);

// Utility to capture URL changes programmatically
const trackUrlChanges = (callback: () => void) => {
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function (...args) {
    originalPushState.apply(this, args);
    callback();
  };

  history.replaceState = function (...args) {
    originalReplaceState.apply(this, args);
    callback();
  };

  window.addEventListener('popstate', callback);

  return () => {
    history.pushState = originalPushState;
    history.replaceState = originalReplaceState;
    window.removeEventListener('popstate', callback);
  };
};

const getParamsMedicalTitle = (paramNames: string[]) => {
  const params = new URLSearchParams(window.location.search);
  return paramNames.map(name => params.getAll(name).join(isHebrewText(name) ? ' ,' : ', '));
};

function NoResultsBoundary({ children, fallback }: { children: React.ReactNode; fallback: React.ReactNode }) {
  const { results } = useInstantSearch();

  const extendedResults = results as typeof results & {
    __isError?: boolean;
    __errorStatus?: number;
  };

  if (extendedResults.__isError && extendedResults.__errorStatus === 403) {
    return (
      <div className="noMessagesWrapper">
        {/* <SvgGenerator
          name="Magnifier"
          className="magnifierIcon"
          objectName="medicalCenterSearchResults"
        /> */}
        <Text text={strings.globalStrings.networkError} />
      </div>
    );
  }

  if (!results.__isArtificial && results.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }

  return <>{children}</>;
}

function NoResults() {
  const noResultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (noResultsRef.current) {
      noResultsRef.current.focus();
    }
  }, []);

  return (
    <div
      className="noMessagesWrapper"
      role="status"
      aria-live="assertive"
      tabIndex={-1}
      ref={noResultsRef}
    >
      <SvgGenerator name="Magnifier" className="magnifierIcon" objectName="doctorSearchResults" />
      <Text text={strings.globalStrings.noResultsFound} />
    </div>
  );
}


export function OuterServicesSearchResults() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const isGeoFromAddress = new URLSearchParams(window.location.search).get('geo') === '1';
  const [currentSortBy, setCurrentSortBy] = useState(isGeoFromAddress ? MEDICALCENTERS_SORT : MEDICALCENTERS);
  const [medicalServices, setMedicalServices] = useState<string | null>('');
  const popupRef = useRef<HTMLDivElement>(null);
  const [key, setKey] = useState(0); // State to force re-render

  useEffect(() => {
    const isGeo = new URLSearchParams(window.location.search).get('geo') === '1';
    if (isGeo) setCurrentSortBy(MEDICALCENTERS_SORT);
  }, []);


  const RenderResults = ({ currentSortBy }: { currentSortBy: string }) => {
    const [hitsResults, setHitsResults] = useState<{ indexName: string, total: number }[]>([
      { indexName: 'MEDICALCENTERS_INDEX', total: 0 },
      { indexName: 'exact', total: 0 },
      { indexName: 'geo', total: 0 }
    ]);
    const [medicalHits, setMedicalCenterHits] = useState(0);
    const [exactHits, setExactHits] = useState(0);
    const [geoHits, setGeoHits] = useState(0);
    const { scopedResults } = useInstantSearch();

    // Sync state with URL query parameters
    useEffect(() => {
      const handleURLChange = () => {
        const updatedServices = getParamsMedicalTitle(['medicalServices'])[0];
        setMedicalServices(updatedServices);
      };

      // Start tracking URL changes
      const stopTracking = trackUrlChanges(handleURLChange);

      // Initialize with current URL
      handleURLChange();

      // Cleanup on unmount
      return stopTracking;
    }, []);

    useEffect(() => {
      // This function will be called whenever scopedResults changes.
      function calculateExactStats() {
        let hitsIndexes = [
          { indexName: 'MEDICALCENTERS_INDEX', total: 0 },
          { indexName: 'exact', total: 0 },
          { indexName: 'geo', total: 0 }
        ];

        let medicalCenterIndex = scopedResults.filter(i => i.indexId === MEDICALCENTERS);
        let exactindex = scopedResults.filter(i => i.indexId === 'exact');
        let geoindex = scopedResults.filter(i => i.indexId === 'geo');

        if (medicalCenterIndex.length) {
          const newHits = medicalCenterIndex[0].results?.nbHits || 0;

          if (hitsIndexes[0].total !== newHits) {
            setMedicalCenterHits(newHits);
            hitsIndexes[0].total = newHits;
          }
        }

        if (exactindex.length) {
          const newExactHits = exactindex[0].results?.nbHits || 0;

          if (hitsIndexes[1].total !== newExactHits) {
            setExactHits(newExactHits);
            hitsIndexes[1].total = newExactHits;
          }
        }
        if (geoindex.length) {
          const newGeoHits = geoindex[0].results?.nbHits || 0;

          if (hitsIndexes[2].total !== newGeoHits) {
            setGeoHits(newGeoHits);
            hitsIndexes[2].total = newGeoHits;
          }
        }

        return hitsIndexes;
      }

      // Calculate the exact stats and update the state.
      //const newHitsResults = calculateExactStats();
      const newHitsResults = calculateExactStats();
      if (newHitsResults !== hitsResults) {
        setHitsResults(newHitsResults);
      }
      if (!isGeoFromAddress && newHitsResults[1].total >= 5) {
        const url = new URL(window.location.href);
        
        const hasCityCode = url.searchParams.has('cityCode');
        const hasServiceCode = url.searchParams.has('serviceCode');
        const hasMedicalServices = url.searchParams.has('medicalServices');
        
        if (!hasCityCode && !hasServiceCode && !hasMedicalServices) {
          url.searchParams.delete('loc');
          url.searchParams.delete('r');
          window.history.replaceState({}, document.title, url.toString());
          setKey(prevKey => prevKey + 1);
        }
      }


    }, [scopedResults, medicalHits, exactHits, geoHits]); // Only re-run the effect if scopedResults changes



    localStorage.setItem('hitsResults', JSON.stringify(hitsResults));

    // if (status !== 'idle' && !isFinalResults) {
    //   // Render a loading spinner or placeholder while waiting for results
    //   return (
    //     <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }} >
    //       <img src={Loader} />
    //     </div>
    //   );
    // }

    if (!checkIfParamExist('loc')) {
      return <InfiniteHitsComponent />
    }

    else if (hitsResults[1].total < 5 && checkIfParamExist('cityCode')) {
      return <><Index indexName={MEDICALCENTERS} indexId="exact">
        <Configure aroundLatLng={checkIfParamExist('loc') ? getParams(['loc'])[0] : ''} aroundRadius={checkIfParamExist('r') ? Number(getParams(['r'])[0]) : 10000}   getRankingInfo={true}/>
        <VirtualRefinementList attribute="cityCode" />
        <VirtualRefinementList attribute="medicalServices" />
        <VirtualRefinementList attribute="serviceCode" /> 
        <GeoInfiniteHitsComponent />
        {hitsResults[2].total > 0 && <div className="resultsAround">
          <SvgGenerator name="Exclamation" className="exclamationIcon" objectName="medicalCenterSearchResults" />
          {/* <Exclamation className="exclamationIcon" /> */}
          <Text text={hitsResults[1].total == 0 ? strings.globalStrings.foundResultsAroundLocation : strings.globalStrings.foundMoreResultsAroundLocation} />
        </div>
        }
      </Index>
        <Index indexName={MEDICALCENTERS} indexId="geo">
          <Configure aroundLatLng={checkIfParamExist('loc') ? getParams(['loc'])[0] : ''} aroundRadius={checkIfParamExist('r') ? Number(getParams(['r'])[0]) : 10000}   getRankingInfo={true} />
          <VirtualSortBy items={[{ label: "שם", value: MEDICALCENTERS }, { label: "מרחק", value: MEDICALCENTERS_SORT }]} sortBy={currentSortBy} />
          <GeoInfiniteHitsComponent />
        </Index>
      </>
    }
    else {
      return (
        <>
          <Index indexName={MEDICALCENTERS} indexId="exact">
            <Configure aroundLatLng={checkIfParamExist('loc') ? getParams(['loc'])[0] : ''} aroundRadius={checkIfParamExist('r') ? Number(getParams(['r'])[0]) : 10000}   getRankingInfo={true}/>
            <VirtualRefinementList attribute="cityCode" />
            <VirtualRefinementList attribute="medicalServices" />
            <VirtualRefinementList attribute="serviceCode" /> 
            <InfiniteHitsComponent />
          </Index>
        </>
      );
    }

  }
  function GeoInfiniteHitsComponent(props: UseInfiniteHitsProps) {
    const { hits, showMore, isLastPage } = useInfiniteHits(props);
    return (
      <div>
        {hits.map((hit: Hit<BaseHit>) => {
          return <OuterServicesResult hit={hit as OuterServiceResultType} key={hit.objectID} />;
        })
        }
        {!isLastPage && <button className='ais-InfiniteHits-loadMore' onClick={showMore} disabled={isLastPage}>{strings.globalStrings.moreResults}</button>}
      </div>
    )
  }

  function InfiniteHitsComponent(props: UseInfiniteHitsProps) {

    const { hits, showMore, isLastPage } = useInfiniteHits(props);
    const [nextFocusIndex, setCurrentFocusIndex] = React.useState(0);
    const outerServiceResultRef = useRef<HTMLDivElement>(null);

    const handleShowMore = () => {
      showMore();
      setCurrentFocusIndex(nextFocusIndex + 20);
      outerServiceResultRef.current?.focus();
    }
    return (
      <div className='animate'>
        {hits.map((hit: Hit<BaseHit>, index) => {
          return <OuterServicesResult hit={hit as OuterServiceResultType} key={`${hit.objectID}_${index}`} ref={index == nextFocusIndex ? outerServiceResultRef : null} />;
        })
        }
        {!isLastPage && <button className='ais-InfiniteHits-loadMore' onClick={handleShowMore} disabled={isLastPage}>{strings.globalStrings.moreResults}</button>}
      </div>
    )
  }

  type TopRefinementProps = {
    setCurrentSortBy: React.Dispatch<React.SetStateAction<string>>;
  };

  const TopRefinement: React.FC<TopRefinementProps> = ({ setCurrentSortBy }) => {
    const storedHitsResults = localStorage.getItem('hitsResults') || '';
    const resultsTotal = storedHitsResults ? JSON.parse(storedHitsResults).map((res: any) => res.total > 0) : -1;

    return (
      resultsTotal ? (
        <><div className={styles.container}>
          <div className={styles.divResults}>
            <Stats />
            {checkIfParamExist('loc') && !isMobile ? (
              <>
                <Text
                  className={styles.text}
                  text={`${strings.globalStrings.sortBy}`}
                />
                <CustomSortBy
                  items={[
                    { label: "שם", value: MEDICALCENTERS },
                    { label: "מרחק", value: MEDICALCENTERS_SORT },
                  ]}
                  onRefine={setCurrentSortBy}
                />
              </>
            ) : null}
          </div>

        </div>
          {checkIfParamExist('medicalServices') && (
            <p className={styles.subTitleMedical}>
              {strings.searchOuterServices.resultsForSearchedMedicalField}{' '}
              <bdi dir="auto">{decodeURIComponent(sanitizeString(medicalServices || ''))}</bdi>
            </p>
          )}

          {strings.medicalCentersSearchResults.topExplanation &&
            <div className={getAllClassNames([
              styles.divTopExpaination,
            ])}>
              <SvgGenerator name="Exclamation" className="exclamationIcon" objectName="medicalCenterSearchResults" />
              <Text className="text" text={strings.medicalCentersSearchResults.topExplanation} />
            </div>
          }
        </>

      ) : null
    );
  };

  // const Stats = () => {
  //   const { nbHits } = useStats();
  //   if (!nbHits) return null;
  //   return (
  //     <div className={styles.result}>
  //       {nbHits === 1 ?
  //         <Text
  //           className={styles.title}
  //           text={strings.globalStrings.foundOneResult} /> :
  //         <Text
  //           className={styles.title}
  //           text={`${strings.medicalCentersSearchResults.found} ${nbHits} ${strings.medicalCentersSearchResults.results}`}
  //         />
  //       }
  //       {/* {checkIfParamExist('medicalServices') && <p className={styles.subTitleMedical}>{`${strings.searchOuterServices.resultsForSearchedMedicalField} ${decodeURIComponent(sanitizeString(medicalServices || ''))}`}</p>} */}
  //     </div>
  //   );
  // };

  const Stats = () => {
    const { nbHits } = useStats();
  
    // hitsResults: [0]=index ראשי (לא מסונן), [1]=exact, [2]=geo.
    // כשיש loc ה-hits מגיעים מ-exact המסונן, אז קוראים משם ולא מ-nbHits של הראשי.
    const stored = localStorage.getItem('hitsResults') || '';
    const parsed = stored ? JSON.parse(stored) : [];
  
    const displayHits = checkIfParamExist('loc')
      ? (parsed[1]?.total || 0)
      : nbHits;
  
    if (!displayHits) return null;
  
    return (
      <div className={styles.result}>
        {displayHits === 1 ?
          <Text
            className={styles.title}
            text={strings.globalStrings.foundOneResult} /> :
          <Text
            className={styles.title}
            text={`${strings.medicalCentersSearchResults.found} ${displayHits} ${strings.medicalCentersSearchResults.results}`}
          />
        }
      </div>
    );
  };

  const handleResize = () => {
    setIsMobile(window.innerWidth < 992);
  };

  useEffect(() => {
    // Set up the event listener
    window.addEventListener('resize', handleResize);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };

  }, []);

  const handleToggleVisibility = () => {
    if (popupRef.current) {
      popupRef.current.classList.toggle('show');
    }
  };

  return (
    <div className="row ais-InstantSearch" key={key}>
      <InstantSearch
        indexName={MEDICALCENTERS}
        searchClient={leumitSearchClient}
        routing={routing}
      >
        <Configure snippetEllipsisText="…" hitsPerPage={20} />
        {isMobile &&
          <Popup
            ref={popupRef}
            onClose={() => handleToggleVisibility()}
          >
            <OuterServicesRefinements />
          </Popup>

        }
        {!isMobile && <OuterServicesRefinements />
        }

        <div className="col-12 col-lg-9 ais-SearchResults">
          {isMobile &&
            <div className={getAllClassNames([styles.divMobileTop])}>
              <FilterButton onClick={() => handleToggleVisibility()} text={strings.globalStrings.filterBy} />
              {checkIfParamExist('loc') && <CustomSortDropdown items={[
                { label: "שם", value: MEDICALCENTERS, items: [] },
                { label: "מרחק", value: MEDICALCENTERS_SORT, items: [] },
              ]}
                onSortChange={setCurrentSortBy} // Add this prop
              />
              }
            </div>
          }
          <TopRefinement setCurrentSortBy={setCurrentSortBy} />
          <VirtualRefinementList attribute="cityCode" />
          <VirtualRefinementList attribute="medicalServices" />
          <VirtualRefinementList attribute="serviceCode" />
          <VirtualQuery query={localAttributes.localDoctorFieldName} />
          <NoResultsBoundary fallback={<NoResults />}>
            <RenderResults currentSortBy={currentSortBy} />
          </NoResultsBoundary>
        </div>
      </InstantSearch>
    </div>
  );
}
